import { isAxiosError } from "axios";
import { CHAT_ERROR_CODES, type ChatErrorCode } from "../contract/chat.events";

/** Server codes (CONTRACT §1) plus the ones only the client can produce. */
export type ChatClientErrorCode =
  | ChatErrorCode
  | "CHAT_NETWORK"
  | "CHAT_TIMEOUT"
  | "CHAT_TOO_MANY_FILES"
  | "CHAT_FILE_TOO_LARGE"
  | "CHAT_UNSUPPORTED_FORMAT"
  | "CHAT_UPLOADS_UNAVAILABLE"
  | "CHAT_UPLOAD_FAILED"
  | "CHAT_GENERIC";

export class ChatError extends Error {
  readonly code: ChatClientErrorCode;
  readonly status: number | null;

  constructor(code: ChatClientErrorCode, status: number | null = null) {
    // The message is the code on purpose: technical texts never reach the UI
    // or the logs, the UI translates `code` (chatErrorKey).
    super(code);
    this.name = "ChatError";
    this.code = code;
    this.status = status;
  }
}

const SERVER_CODES: ReadonlySet<string> = new Set(CHAT_ERROR_CODES);

export function isChatErrorCode(value: unknown): value is ChatErrorCode {
  return typeof value === "string" && SERVER_CODES.has(value);
}

/** REST error / socket ACK error / network failure → ChatError. */
export function toChatError(error: unknown): ChatError {
  if (error instanceof ChatError) return error;

  if (isAxiosError(error)) {
    const status = error.response?.status ?? null;
    const code: unknown = (error.response?.data as { code?: unknown } | undefined)?.code;
    if (isChatErrorCode(code)) return new ChatError(code, status);
    if (error.code === "ERR_CANCELED") return new ChatError("CHAT_GENERIC", status);
    if (!error.response) return new ChatError("CHAT_NETWORK");
    if (status === 401) return new ChatError("CHAT_UNAUTHORIZED", status);
    if (status === 403) return new ChatError("CHAT_FORBIDDEN", status);
    if (status === 429) return new ChatError("CHAT_RATE_LIMITED", status);
    if (status !== null && status >= 500) return new ChatError("CHAT_INTERNAL", status);
    return new ChatError("CHAT_GENERIC", status);
  }

  if (typeof error === "object" && error !== null && "code" in error) {
    const code = (error as { code: unknown }).code;
    if (isChatErrorCode(code)) return new ChatError(code);
    if (code === "CHAT_NETWORK" || code === "CHAT_TIMEOUT") return new ChatError(code);
  }

  return new ChatError("CHAT_GENERIC");
}

/** `CHAT_LISTING_NOT_FOUND` → `chat.errors.listingNotFound`. */
export function chatErrorKey(code: ChatClientErrorCode): string {
  const camel = code
    .replace(/^CHAT_/, "")
    .toLowerCase()
    .replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  return `chat.errors.${camel}`;
}

/** Failures that may pass on their own — worth retrying with backoff. */
const RETRIABLE: ReadonlySet<ChatClientErrorCode> = new Set<ChatClientErrorCode>([
  "CHAT_NETWORK",
  "CHAT_TIMEOUT",
  "CHAT_RATE_LIMITED",
  "CHAT_INTERNAL",
]);

export function isRetriable(code: ChatClientErrorCode): boolean {
  return RETRIABLE.has(code);
}

/** TanStack Query `retry`: never 401/403/404 or client errors; network/5xx up to 3 times. */
export function shouldRetryChatQuery(failureCount: number, error: unknown): boolean {
  return failureCount < 3 && isRetriable(toChatError(error).code);
}

/** Exponential backoff with jitter, capped at 30 s. */
export function backoffDelay(attempt: number): number {
  const base = Math.min(30_000, 1000 * 2 ** Math.max(0, attempt));
  return Math.round(base / 2 + Math.random() * (base / 2));
}
